/**
 * Fallback rendered by the top-level Sentry error boundary when an
 * uncaught render error escapes the app. The goal is to give users a
 * recoverable, branded screen with enough detail (Sentry event id, error
 * message) for support to act on, instead of the legacy generic white screen.
 *
 * @author Milton Filho
 */
import React from 'react';
import { Box, Button, Flex, Heading, Text } from 'rebass';
import { ThemeProvider } from 'theme-ui';
import { theme } from 'theme/theme';
import { BACKGROUND_GRADIENT } from 'theme/constants';
import { logout } from 'modules/auth/actions';
import store from 'store/store';

interface Props {
  error: unknown;
  eventId?: string | null;
  resetError: () => void;
}

const getErrorMessage = (error: unknown): string => {
  if (!error) return 'Unknown error';
  if (error instanceof Error) return error.message || error.name;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const handleReload = () => {
  window.location.reload();
};

const handleSignOut = () => {
  store.dispatch(logout());
  window.location.href = '/';
};

export const AppErrorFallback: React.FC<Props> = ({
  error,
  eventId,
  resetError,
}) => {
  const message = getErrorMessage(error);

  return (
    <ThemeProvider theme={theme}>
      <Flex
        sx={{
          position: 'fixed',
          inset: 0,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 4,
          backgroundColor: 'background',
          color: 'customDarkBlue',
          textAlign: 'center',
          overflow: 'auto',
        }}>
        <Box
          sx={{
            width: '64px',
            height: '8px',
            borderRadius: 'borderRadius',
            background: BACKGROUND_GRADIENT,
            marginBottom: 4,
          }}
        />
        <Heading
          as="h1"
          sx={{
            fontSize: [4, 5],
            marginBottom: 2,
            color: 'customDarkBlue',
          }}>
          Something went wrong
        </Heading>
        <Text
          sx={{
            fontSize: 2,
            color: 'customGrey',
            maxWidth: '480px',
            marginBottom: 3,
            lineHeight: 1.5,
          }}>
          The app hit an unexpected error and could not finish loading this
          screen. Your offline changes are still saved and will sync when you
          retry.
        </Text>
        <Box
          sx={{
            backgroundColor: 'faded',
            borderRadius: 'borderRadius',
            padding: 3,
            marginBottom: 4,
            maxWidth: '480px',
            width: '100%',
            textAlign: 'left',
          }}>
          <Text
            sx={{
              fontSize: 0,
              fontWeight: 'bold',
              color: 'customGrey',
              marginBottom: 1,
            }}>
            Error details
          </Text>
          <Text
            sx={{
              fontFamily: 'monospace',
              fontSize: 1,
              color: 'customDarkBlue',
              wordBreak: 'break-word',
            }}>
            {message}
          </Text>
          {eventId && (
            <Text
              sx={{
                fontSize: 0,
                color: 'customGrey',
                marginTop: 2,
              }}>
              Reference ID: <span style={{ fontFamily: 'monospace' }}>{eventId}</span>
            </Text>
          )}
        </Box>
        <Flex
          sx={{
            flexDirection: ['column', 'row'],
            gap: 2,
            width: '100%',
            maxWidth: '480px',
          }}>
          <Button
            type="button"
            onClick={resetError}
            variant="primary"
            sx={{ flex: 1, cursor: 'pointer' }}>
            Try again
          </Button>
          <Button
            type="button"
            onClick={handleReload}
            variant="secondary"
            sx={{ flex: 1, cursor: 'pointer' }}>
            Reload app
          </Button>
          <Button
            type="button"
            onClick={handleSignOut}
            variant="link"
            sx={{ flex: 1, cursor: 'pointer' }}>
            Sign out
          </Button>
        </Flex>
      </Flex>
    </ThemeProvider>
  );
};
